import api from './api';
import {
  BatchImportWithItems,
  BatchImportItem,
  BatchImportListResponse,
  BatchImportItemsListResponse,
  BatchImportPreviewResponse,
  BatchImportExecuteResponse,
  BatchImportItemUpdate,
  ClusterInfo,
  ClustersListResponse,
} from '../types';

export const batchImportService = {
  /**
   * Get list of available TTI clusters/regions
   */
  async getClusters(): Promise<ClusterInfo[]> {
    const response = await api.get<ClustersListResponse>('/api/batch-imports/clusters');
    return response.data.clusters;
  },

  /**
   * Preview a CSV batch import (creates staged batch)
   */
  async previewImport(
    file: File, 
    appId: string, 
    name?: string, 
    defaultCluster: string = 'eu1'
  ): Promise<BatchImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('app_id', appId);
    if (name) {
      formData.append('name', name);
    }
    formData.append('default_cluster', defaultCluster);
    
    const response = await api.post<BatchImportPreviewResponse>(
      '/api/batch-imports/preview',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * List all batch imports with pagination
   */
  async listBatchImports(
    page: number = 1,
    perPage: number = 20,
    statusFilter?: string
  ): Promise<BatchImportListResponse> {
    const params: Record<string, string | number> = { page, per_page: perPage };
    if (statusFilter) {
      params.status_filter = statusFilter;
    }
    const response = await api.get<BatchImportListResponse>('/api/batch-imports', { params });
    return response.data;
  },

  /**
   * Get a specific batch import with all items
   */
  async getBatchImport(batchId: number): Promise<BatchImportWithItems> {
    const response = await api.get<BatchImportWithItems>(`/api/batch-imports/${batchId}`);
    return response.data;
  },

  /**
   * Get paginated items for a batch import
   */
  async getBatchImportItems(
    batchId: number,
    page: number = 1,
    perPage: number = 50,
    statusFilter?: string
  ): Promise<BatchImportItemsListResponse> {
    const params: Record<string, string | number> = { page, per_page: perPage };
    if (statusFilter) {
      params.status_filter = statusFilter;
    }
    const response = await api.get<BatchImportItemsListResponse>(
      `/api/batch-imports/${batchId}/items`,
      { params }
    );
    return response.data;
  },

  /**
   * Update a batch import item
   */
  async updateBatchImportItem(
    batchId: number,
    itemId: number,
    data: BatchImportItemUpdate
  ): Promise<BatchImportItem> {
    const response = await api.patch<BatchImportItem>(
      `/api/batch-imports/${batchId}/items/${itemId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a batch import item
   */
  async deleteBatchImportItem(batchId: number, itemId: number): Promise<void> {
    await api.delete(`/api/batch-imports/${batchId}/items/${itemId}`);
  },

  /**
   * Execute a staged batch import
   */
  async executeBatchImport(batchId: number): Promise<BatchImportExecuteResponse> {
    const response = await api.post<BatchImportExecuteResponse>(
      `/api/batch-imports/${batchId}/execute`
    );
    return response.data;
  },

  /**
   * Delete/cancel a batch import
   */
  async deleteBatchImport(batchId: number): Promise<void> {
    await api.delete(`/api/batch-imports/${batchId}`);
  },

  /**
   * Revalidate all items in a batch import
   */
  async revalidateBatchImport(batchId: number): Promise<BatchImportWithItems> {
    const response = await api.post<BatchImportWithItems>(
      `/api/batch-imports/${batchId}/revalidate`
    );
    return response.data;
  },

  /**
   * Retry failed items in a completed batch import.
   * If itemIds is provided, only those items are retried; otherwise all failed items are retried.
   */
  async retryBatchImport(batchId: number, itemIds?: number[]): Promise<BatchImportExecuteResponse> {
    const response = await api.post<BatchImportExecuteResponse>(
      `/api/batch-imports/${batchId}/retry`,
      itemIds && itemIds.length > 0 ? { item_ids: itemIds } : {}
    );
    return response.data;
  },
};
